import { useMemo } from "react";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import {
  getCostTypeLabel,
  getCostTypeIcon,
  getCostTypeColor,
} from "../utils/costTypeConfig";
import { Money } from "./Money";

type AdditionalCost = {
  id: string;
  cost_type: string;
  label: string | null;
  amount: number;
};

type Item = {
  id: string;
  unit_cost?: number;
  overridden_unit_price?: number;
  line_cost_total?: number;
  ordered_quantity: number;
  allocated_additional_cost?: number;
  landed_unit_cost?: number;
};

interface LandedCostCellProps {
  item: Item;
  additionalCosts: AdditionalCost[];
  subtotal: number;
  /** BLU-373 PR2b: PO currency code for the Money wrapper */
  poCurrency: string | null;
  /** BLU-373 PR2b: PO exchange rate so cells flip with the toggle */
  exchangeRate: number | null;
}

export function LandedCostCell({
  item,
  additionalCosts,
  subtotal,
  poCurrency,
  exchangeRate,
}: LandedCostCellProps) {
  const breakdown = useMemo(() => {
    const effectiveUnit = item.overridden_unit_price ?? item.unit_cost ?? null;
    const lineCost = item.line_cost_total ?? (effectiveUnit != null ? effectiveUnit * item.ordered_quantity : null);
    const allocatedTotal = item.allocated_additional_cost ?? 0;
    const landedUnit = item.landed_unit_cost ?? effectiveUnit;
    const landedTotal = lineCost != null ? (lineCost + allocatedTotal) : null;

    // Per-cost-type breakdown (proportional)
    const itemShare = subtotal > 0 && lineCost != null ? lineCost / subtotal : 0;
    const costBreakdown = additionalCosts.map((cost) => ({
      id: cost.id,
      costType: cost.cost_type,
      label: cost.cost_type === "other" && cost.label ? cost.label : getCostTypeLabel(cost.cost_type),
      allocatedAmount: cost.amount * itemShare,
    }));

    return { effectiveUnit, lineCost, allocatedTotal, landedUnit, landedTotal, costBreakdown };
  }, [item, additionalCosts, subtotal]);

  return (
    <div className="flex items-center justify-end gap-1">
      <Money
        amount={breakdown.landedTotal}
        poCurrency={poCurrency}
        exchangeRate={exchangeRate}
        className="text-sm font-medium"
      />
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
            aria-label="Cost breakdown"
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-64 p-3"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Unit Cost</span>
              <Money amount={breakdown.effectiveUnit} poCurrency={poCurrency} exchangeRate={exchangeRate} />
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Line Cost</span>
              <Money amount={breakdown.lineCost} poCurrency={poCurrency} exchangeRate={exchangeRate} />
            </div>

            {breakdown.costBreakdown.length > 0 && (
              <>
                <Separator />
                <p className="text-xs text-muted-foreground">Allocated costs (approx.)</p>
                {breakdown.costBreakdown.map((cost) => {
                  const Icon = getCostTypeIcon(cost.costType);
                  return (
                    <div key={cost.id} className="flex justify-between items-center">
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Icon className={`h-3 w-3 ${getCostTypeColor(cost.costType)}`} aria-hidden="true" />
                        {cost.label}
                      </span>
                      <Money
                        amount={cost.allocatedAmount}
                        poCurrency={poCurrency}
                        exchangeRate={exchangeRate}
                        className="text-muted-foreground"
                      />
                    </div>
                  );
                })}
              </>
            )}

            <Separator />
            <div className="flex justify-between font-medium">
              <span>Landed Unit</span>
              <Money amount={breakdown.landedUnit} poCurrency={poCurrency} exchangeRate={exchangeRate} />
            </div>
            <div className="flex justify-between font-medium">
              <span>Landed Total</span>
              <Money amount={breakdown.landedTotal} poCurrency={poCurrency} exchangeRate={exchangeRate} />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
